import React, { useState } from 'react';
import { uploadData } from 'aws-amplify/storage';
import { fetchAuthSession , getCurrentUser} from 'aws-amplify/auth';
import './DataUpload.css';

const DataUpload = () => {
  const [file, setFile] = useState(null);
  const [launchMonitor, setLaunchMonitor] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const launchMonitors = [
    { value: 'trackman', label: 'Trackman' },
    { value: 'gcquad', label: 'GC Quad' },
    { value: 'flightscope', label: 'Flightscope' },
    { value: 'skytrak', label: 'SkyTrak' },
    { value: 'garminr10', label: 'Garmin R10' },
    { value: 'rapsodo', label: 'Rapsodo' }
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    if (!launchMonitor) {
      setError('Please select a launch monitor type');
      return;
    }

    setError('');
    setSuccess('');
    setUploading(true);
    setProgress(0);

    try {
      // Ensure we have a valid auth session before uploading
      const session = await fetchAuthSession();
      if (!session.tokens) {
        throw new Error('No valid authentication session');
      }

      const userData = await getCurrentUser();
      // Create a unique filename with timestamp
      const timestamp = new Date().getTime();
      const filename = `uploads/${userData.userId}/${timestamp}_${file.name}`;

      // Upload to S3
      await uploadData({
        key: filename,
        data: file,
        options: {
          accessLevel: 'private',
          contentType: file.type,
          onProgress: (progress) => {
            const progressPercentage = Math.round((progress.loaded / progress.total) * 100);
            setProgress(progressPercentage);
          }
        }
      });

      setSuccess('File uploaded successfully!');
      setFile(null);
      // Reset the file input
      document.getElementById('file-upload').value = '';
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="data-upload-container">
      {error && <div className="upload-error">{error}</div>}
      {success && <div className="upload-success">{success}</div>}

      <form onSubmit={handleUpload}>
        <div className="form-group">
          <label htmlFor="launch-monitor">Launch Monitor Type</label>
          <select
            id="launch-monitor"
            value={launchMonitor}
            onChange={(e) => setLaunchMonitor(e.target.value)}
            disabled={uploading}
            required
          >
            <option value="">Select Launch Monitor</option>
            {launchMonitors.map(monitor => (
              <option key={monitor.value} value={monitor.value}>
                {monitor.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="file-upload">Select Data File</label>
          <input
            type="file"
            id="file-upload"
            onChange={handleFileChange}
            disabled={uploading}
            accept=".csv,.xlsx,.xls,.txt,.json"
          />
          <small>Supported formats: CSV, Excel, TXT, JSON</small>
        </div>

        {file && (
          <div className="file-info">
            <p>Selected file: {file.name}</p>
            <p>Size: {(file.size / 1024).toFixed(2)} KB</p>
          </div>
        )}

        {uploading && (
          <div className="progress-container">
            <div
              className="progress-bar"
              style={{ width: `${progress}%` }}
            ></div>
            <span className="progress-text">{progress}%</span>
          </div>
        )}

        <button
          type="submit"
          className="btn-upload"
          disabled={uploading || !file}
        >
          {uploading ? 'Uploading...' : 'Upload Data'}
        </button>
      </form>

      <div className="upload-instructions">
        <h4>How to Export Data</h4>
        <p>To export data from your launch monitor:</p>
        <ol>
          <li>Open your launch monitor's software or app</li>
          <li>Navigate to your session data</li>
          <li>Look for an "Export" or "Download" option</li>
          <li>Save the file to your device</li>
          <li>Upload the file using the form above</li>
        </ol>
        <p>For detailed instructions for specific launch monitors, visit the Documentation section.</p>
      </div>
    </div>
  );
};

export default DataUpload;
